export const PERSONS = ["Steven", "Richard", "Both"];
export const LOCATIONS = ["Office", "Home", "Remote", "Off-site meeting", "Other"];
export const OFFICE_STATUS = ["Planned", "Confirmed", "Changed", "Cancelled"];
export const WORKSTREAMS = ["Product and Content", "Launch", "Marketing", "Investors", "Finance", "Operations", "Future Innovations", "Other"];
export const asOpts = (a) => a.map((v) => ({ value: v, label: v }));

// Founder identity — never colour alone: always paired with initials/name.
export const personMeta = (p) => ({
  Steven: { initials: "S", name: "Steven", color: "#1D4ED8", tint: "#EAF0FF", ring: "#1D4ED8" },
  Richard: { initials: "R", name: "Richard", color: "#0D9488", tint: "#E6FBF8", ring: "#0D9488" },
  Both: { initials: "S+R", name: "Both", color: "#F97316", tint: "#FFF3E9", ring: "#F97316" },
}[p] || { initials: "?", name: p, color: "#5A6B8C", tint: "#EEF2FB", ring: "#5A6B8C" });

export const officeStatusBadge = (s) => ({ Planned: "info", Confirmed: "success", Changed: "warning", Cancelled: "neutral" }[s] || "neutral");

// ---------- date / week helpers (local, no TZ surprises) ----------
const pad = (n) => String(n).padStart(2, "0");
export const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayIso = () => isoOf(new Date());
export const parseIso = (iso) => new Date(`${iso}T00:00:00`);
export const addDays = (iso, n) => { const d = parseIso(iso); d.setDate(d.getDate() + n); return isoOf(d); };
export const mondayOf = (iso) => { const d = parseIso(iso); const off = (d.getDay() + 6) % 7; d.setDate(d.getDate() - off); return isoOf(d); };
export const weekDays = (monIso) => Array.from({ length: 7 }, (_, i) => addDays(monIso, i));
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const dowLabel = (iso) => DOW[(parseIso(iso).getDay() + 6) % 7];
export const dayNum = (iso) => parseIso(iso).getDate();
export const rangeLabel = (monIso) => {
  const a = parseIso(monIso); const b = parseIso(addDays(monIso, 6));
  const same = a.getMonth() === b.getMonth();
  return same
    ? `${a.getDate()}–${b.getDate()} ${MON[a.getMonth()]} ${a.getFullYear()}`
    : `${a.getDate()} ${MON[a.getMonth()]} – ${b.getDate()} ${MON[b.getMonth()]} ${b.getFullYear()}`;
};

// ---------- time helpers ----------
export const toMin = (t) => { const [h, m] = String(t).split(":"); return (+h) * 60 + (+m); };
export const durH = (s, e) => Math.max(0, (toMin(e) - toMin(s)) / 60);
export const fmtRange = (s, e) => `${s}–${e}`;
export const fmtHours = (h) => `${Number.isInteger(h) ? h : h.toFixed(1)}h`;

// founders present in an entry
const founders = (p) => (p === "Both" ? ["Steven", "Richard"] : [p]);
const rangesOverlap = (s1, e1, s2, e2) => toMin(s1) < toMin(e2) && toMin(s2) < toMin(e1);

// Overlap warning: same founder, same date, overlapping time (exclude cancelled + self)
export const findOverlaps = (entry, all) => all.filter((o) =>
  o.id !== entry.id && o.status !== "Cancelled" && o.date === entry.date &&
  founders(o.person).some((f) => founders(entry.person).includes(f)) &&
  rangesOverlap(entry.start_time, entry.end_time, o.start_time, o.end_time));

// Duplicate warning: identical person/date/time/location (exclude cancelled + self)
export const findDuplicate = (entry, all) => all.find((o) =>
  o.id !== entry.id && o.status !== "Cancelled" && o.date === entry.date &&
  o.person === entry.person && o.start_time === entry.start_time &&
  o.end_time === entry.end_time && o.location === entry.location);

// per-date shared office hours (both founders at Office, overlapping) — client mirror of backend
const merge = (ivs) => { const out = []; ivs.slice().sort((a, b) => a[0] - b[0]).forEach(([s, e]) => { if (out.length && s <= out[out.length - 1][1]) out[out.length - 1][1] = Math.max(out[out.length - 1][1], e); else out.push([s, e]); }); return out; };
const overlapMin = (a, b) => { let t = 0; a.forEach(([s1, e1]) => b.forEach(([s2, e2]) => { t += Math.max(0, Math.min(e1, e2) - Math.max(s1, s2)); })); return t; };
export const sharedOfficeByDate = (entries) => {
  const map = {};
  const dates = [...new Set(entries.map((e) => e.date))];
  dates.forEach((d) => {
    const at = (f) => entries.filter((e) => e.status !== "Cancelled" && e.location === "Office" && e.date === d && founders(e.person).includes(f)).map((e) => [toMin(e.start_time), toMin(e.end_time)]);
    const s = merge(at("Steven")); const r = merge(at("Richard"));
    if (s.length && r.length) { const m = overlapMin(s, r); if (m > 0) map[d] = m / 60; }
  });
  return map;
};
